import React from 'react';
import { ShieldCheck, Building2, HardHat, FileText, Clock } from 'lucide-react';

const ICONS = { ShieldCheck, Building2, HardHat, FileText };

/**
 * Renders an article payload exactly as it will appear on the live site's
 * /resources/:slug page. Kept visually aligned with V2ResourceArticle so
 * the admin sees precisely what will publish.
 */

const Block = ({ block }) => {
  if (block.type === 'p') {
    return <p className="text-slate-700 leading-[1.75] text-[17px] mb-6">{block.text}</p>;
  }
  if (block.type === 'h2') {
    return <h2 className="text-2xl md:text-3xl font-medium text-slate-900 tracking-tight mt-12 mb-5 leading-tight">{block.text}</h2>;
  }
  if (block.type === 'h3') {
    return <h3 className="text-xl font-medium text-slate-900 tracking-tight mt-8 mb-3">{block.text}</h3>;
  }
  if (block.type === 'quote') {
    return (
      <blockquote className="my-8 pl-6 border-l-4 border-blue-600 text-xl md:text-2xl text-slate-900 font-medium italic leading-snug">
        &ldquo;{block.text}&rdquo;
      </blockquote>
    );
  }
  if (block.type === 'list') {
    return (
      <ul className="my-6 space-y-3">
        {block.items.map((it, i) => (
          <li key={i} className="flex gap-3 text-slate-700 leading-[1.7] text-[16.5px]">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-blue-600 flex-shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  return null;
};

const ArticlePreview = ({ article }) => {
  const Icon = ICONS[article.icon] || FileText;
  return (
    <div className="bg-white text-slate-900 rounded-xl overflow-hidden border border-slate-200" data-testid="admin-article-preview">
      <section className="pt-14 md:pt-16 pb-10 border-b border-slate-200 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6">
          <p className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4 inline-flex items-center gap-2">
            <Icon className="w-3.5 h-3.5" /> {article.kicker}
          </p>
          <h1 className="text-3xl sm:text-4xl font-medium tracking-tight text-slate-900 leading-[1.12] mb-5">
            {article.title}
          </h1>
          <p className="text-slate-600 leading-relaxed text-[15px] max-w-2xl">
            {article.excerpt}
          </p>
          <div className="flex items-center gap-4 text-sm text-slate-500 mt-6">
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> {article.readMinutes} min read
            </span>
            <span>·</span>
            <span>Keystone Strategic Consultants</span>
          </div>
        </div>
      </section>
      <article className="py-12">
        <div className="max-w-3xl mx-auto px-6">
          {article.blocks.map((b, i) => (
            <Block key={i} block={b} />
          ))}
        </div>
      </article>
      <section className="py-8 border-t border-slate-200 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">SEO metadata</div>
          <div className="text-sm">
            <div className="mb-2">
              <span className="text-slate-500">Title tag: </span>
              <span className="text-slate-900">{article.seo.title}</span>{' '}
              <span className="text-slate-400">({article.seo.title.length} chars)</span>
            </div>
            <div>
              <span className="text-slate-500">Meta description: </span>
              <span className="text-slate-900">{article.seo.description}</span>{' '}
              <span className="text-slate-400">({article.seo.description.length} chars)</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArticlePreview;
